import Model from 'src/app/common/model';
import PondTypeModel from './pondType.model';
import PondUseModel from './pondUse.model';
import PondSectionModel from './pondSection.model';
import PondCultureModel from './pondCulture.model';
import FarmSiteModel from './farmSite.model';
import CustomerDeviceModel from './customerDevice.model';
import BarcodeModel from './barCode.model';
import SpeciesTypeModel from './speciesType.model';

/**
 * @model CustomerPondModel
 * @description
 * Represents a specific Pond or Tank within a Farm Site in the AquaCloud application.
 * This model is the central entity for production management, serving as the container
 * for all culture activities, device associations, and physical configurations.
 *
 * **Domain Context:**
 * In aquaculture, the "Pond" (or Tank) is where the livestock lives. Managing its
 * physical parameters (size, depth), location, and operational status is critical
 * for calculating stocking density, feed requirements, and chemical dosages.
 *
 * **Purpose:**
 * To encapsulate the static and dynamic attributes of a single production unit.
 * It answers: "How big is this pond?", "Where is it?", "Is it currently in use?",
 * "Who is the investor?", "What devices are monitoring it?".
 *
 * **Key Responsibilities:**
 * 1. **Physical Configuration**: Stores dimensions (`pond_size`, `pond_depth`, `volume`) and construction details (`pond_linear`, `central_drain_system`).
 * 2. **Hierarchy & Location**: Links the pond to a `farm_site_id` and a `section_id`.
 * 3. **Operational State**: Tracks availability (`available`), status, and active culture counts.
 * 4. **Resource Association**: Aggregates assigned IoT devices (`customerDevices`) and barcodes.
 * 5. **Regulatory Compliance**: Stores government registration numbers like `ciba_reg_num`.
 *
 * **Data Flow:**
 * - **Creation**: Defined by the Farm Manager during the "Farm Setup" phase.
 * - **Updates**: Modified when physical changes occur (e.g., re-lining) or when status changes (Active/Inactive).
 * - **Consumption**:
 *   - **Feed Log**: Used to calculate feed per unit area.
 *   - **Dashboards**: Aggregated to show total farm capacity and utilization.
 *   - **IoT**: Used to map sensor readings to a specific location.
 *
 * **User Roles & Permissions:**
 * - **Farm Manager**: Can create, edit, and manage pond details.
 * - **Technician**: Views pond details to perform daily tasks.
 * - **Investor**: May be assigned to specific ponds (`farm_investor_id`) for financial tracking.
 *
 * **Features:**
 * - Supports various pond types (Earthen, Lined, Nursery).
 * - Handles unit conversions (Hectares vs Acres via `isHectares`).
 * - Supports complex ownership models via `farm_investor_id`.
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class CustomerPondModel extends Model {

  /**
   * @type {PondTypeModel}
   * @description Classification of the pond (e.g., Earthen, Lined, Concrete).
   */
  PondType: PondTypeModel;

  /**
   * @type {PondUseModel}
   * @description The intended usage of the pond (e.g., Nursery, Grow-out, Reservoir).
   */
  PondUse: PondUseModel;

  /**
   * @type {PondSectionModel}
   * @description The section or block within the farm where this pond is located.
   */
  PondSection: PondSectionModel;

  /**
   * @type {Array<PondCultureModel>}
   * @description History of culture cycles (crops) associated with this pond.
   */
  PondCultureData: Array<PondCultureModel>;

  /**
   * @type {Array<FarmSiteModel>}
   * @description Details of the parent farm site(s).
   */
  FarmSiteDetails: Array<FarmSiteModel>;

  /**
   * @type {Array<CustomerDeviceModel>}
   * @description List of IoT devices (sensors, aerators) currently assigned to this pond.
   */
  customerDevices: Array<CustomerDeviceModel>;

  /**
   * @type {Array<BarcodeModel>}
   * @description Barcode sequences associated with this pond for inventory or tracking.
   */
  barcodeSequence: Array<BarcodeModel>;

  /**
   * @type {CustomerPondModel}
   * @description Self-reference, sometimes used for recursive structures or linked ponds.
   */
  customerPond: CustomerPondModel;

  /**
   * @type {SpeciesTypeModel}
   * @description The species currently stocked in the pond (if active).
   */
  speciesType: SpeciesTypeModel;

  /**
   * @type {Array<string>}
   * @description List of IDs, often used for bulk operations or filtering.
   * @optional
   */
  ids?: Array<string>;

  /**
   * @type {string}
   * @description CIBA (Central Institute of Brackishwater Aquaculture) Registration Number.
   * Regulatory requirement for Indian aquaculture farms.
   * @optional
   */
  ciba_reg_num?: string;

  /**
   * @type {number}
   * @description Latitude coordinate of the pond center.
   * @optional
   */
  latitude?: number;

  /**
   * @type {number}
   * @description Longitude coordinate of the pond center.
   * @optional
   */
  longitude?: number;

  /**
   * @type {number}
   * @description Foreign key to the GeoLocation table.
   * @optional
   */
  geo_location_id?: number;

  /**
   * @type {string}
   * @description Human-readable name of the pond (e.g., "Pond A1").
   * @optional
   */
  name?: string;

  /**
   * @type {string}
   * @description Unique system code for the pond.
   */
  pond_code: string;

  /**
   * @type {number}
   * @description The surface area of the pond. Unit depends on `isHectares` flag.
   * @optional
   */
  pond_size?: number;

  /**
   * @type {number}
   * @description Foreign key for the Pond Type.
   */
  pond_type_id: number;

  /**
   * @type {number}
   * @description Foreign key for the Pond Use category.
   */
  pond_use_id: number;

  /**
   * @type {number}
   * @description Foreign key for the Section.
   */
  section_id: number;

  /**
   * @type {number}
   * @description Unique numeric identifier for the pond.
   */
  pond_id: number;

  /**
   * @type {string}
   * @description Unique string identifier for the parent Farm Site.
   */
  farm_site_id: string;

  /**
   * @type {string}
   * @description Display name of the section.
   * @optional
   */
  section_name?: string;

  /**
   * @type {number}
   * @description Diameter of the tank if circular (in meters/feet).
   * @optional
   */
  tank_diameter?: number;

  /**
   * @type {number}
   * @description Average depth of the pond (in meters/feet).
   * @optional
   */
  pond_depth?: number;

  /**
   * @type {boolean}
   * @description Flag indicating if the pond has a liner (plastic/concrete) vs earthen bottom.
   * @optional
   */
  pond_linear?: boolean;

  /**
   * @type {boolean}
   * @description Flag indicating if the pond has a central drainage system (common in intensive farming).
   * @optional
   */
  central_drain_system?: boolean;

  /**
   * @type {number}
   * @description Number of aerators installed in the pond.
   * @optional
   */
  number_of_aerators?: number;

  /**
   * @type {boolean}
   * @description Flag indicating the unit of measure for `pond_size`.
   * True = Hectares, False = Acres.
   */
  isHectares: boolean;

  /**
   * @type {boolean}
   * @description Flag indicating if this is a preparatory pond (e.g., for water treatment).
   */
  isPreparatory: boolean;

  /**
   * @type {number}
   * @description Total water volume capacity of the pond.
   */
  volume: number;

  /**
   * @type {string}
   * @description Alternative code or identifier.
   * @optional
   */
  code?: string;

  /**
   * @type {number}
   * @description Maximum carrying capacity (biomass) of the pond.
   * @optional
   */
  capacity?: number;

  /**
   * @type {string}
   * @description Current operational status (e.g., 'Active', 'Harvested', 'Dry').
   * @optional
   */
  status?: string;

  /**
   * @type {number}
   * @description ID of the production unit this pond belongs to.
   * @optional
   */
  production_unit_id?: number;

  /**
   * @type {number}
   * @description ID of the shed if this is an indoor tank.
   * @optional
   */
  shed_id?: number;

  /**
   * @type {boolean}
   * @description Flag indicating if this is a "Blue Hatchery" (BH) unit.
   * @optional
   */
  isBH?: boolean;

  /**
   * @type {string}
   * @description Construction material (e.g., 'Concrete', 'Tarpaulin').
   * @optional
   */
  material?: string;

  /**
   * @type {number}
   * @description Height of the tank walls.
   * @optional
   */
  height?: number;

  /**
   * @type {number}
   * @description Breadth dimension of the pond/tank.
   * @optional
   */
  breadth?: number;

  /**
   * @type {number}
   * @description Width dimension of the pond/tank.
   * @optional
   */
  width?: number;

  /**
   * @type {number}
   * @description ID of the section within a Blue Hatchery facility.
   * @optional
   */
  bh_section_id?: number;

  /**
   * @type {boolean}
   * @description Flag indicating if the pond is available for a new culture cycle.
   * @optional
   */
  available?: boolean;

  /**
   * @type {string}
   * @description ID of the investor or financial stakeholder assigned to this pond.
   * @optional
   */
  farm_investor_id?: string;

  /**
   * @type {number}
   * @description Count of ponds (used in aggregation contexts).
   */
  pondsCount: number;

  /**
   * @type {object}
   * @description Generic object for storing count metrics.
   */
  count: object;

  /**
   * @type {number}
   * @description Total number of ponds in the group/site.
   */
  totalPonds: number;

  /**
   * @type {string}
   * @description Total number of animals stocked across all ponds.
   * Stored as string to handle large numbers or formatting.
   */
  totalStockingCount: string;

  /**
   * @type {number}
   * @description Total area of all ponds combined.
   */
  totalPondSize: number;

  /**
   * @type {number}
   * @description Number of currently inactive ponds.
   */
  inactivePonds: number;

  /**
   * @type {string}
   * @description Stocking count in inactive ponds (historical or remaining).
   */
  inactiveStockingCount: string;

  /**
   * @type {number}
   * @description Total area of inactive ponds.
   */
  inactivePondSize: number;

  /**
   * @type {string}
   * @description Name of the business entity owning the pond.
   */
  businessName: string;

  /**
   * @type {string}
   * @description A unique identifier string for the pond.
   */
  pond_identifier: string;

  /**
   * @type {number}
   * @description Current stocking density (animals per unit area/volume).
   */
  stocking_density: number;

  /**
   * @type {string}
   * @description Display name for the tank.
   * @optional
   */
  TankName?: string;

  /**
   * @type {number}
   * @description Total expense cost incurred for this pond.
   * @optional
   */
  expensecost?: number;

  /**
   * @type {string}
   * @description Name of a specific item associated with the pond (context dependent).
   * @optional
   */
  item_name?: string;

  /**
   * @type {number}
   * @description Percentage metric (e.g., survival rate, utilization).
   * @optional
   */
  percentage?: number;

  /**
   * @type {string}
   * @description ID of the feed provider servicing this pond.
   * @optional
   */
  feedProviderId?: string;

  /**
   * @type {string}
   * @description ID of the farm investor (alias for farm_investor_id).
   * @optional
   */
  farmInvestorId?: string;

  /**
   * @type {number}
   * @description Count of inactive entities related to this pond.
   */
  inActiveCount: number;

  /**
   * @type {number}
   * @description Count of active entities related to this pond.
   */
  activeCount: number;

  /**
   * @type {string}
   * @description Sub-classification of the pond type.
   */
  sub_type: string;

  /**
   * @type {any}
   * @description Water volume metric, potentially with units.
   */
  water_volume: any;

  /**
   * @type {number}
   * @description ID representing the specific use case of the pond.
   */
  ponduse_id: number;

  /**
   * @type {string}
   * @description Name of the farm at this specific location.
   */
  farmNameAtLocation: string;

  /**
   * @constructor
   * @description
   * Initializes a new instance of the CustomerPondModel.
   * Calls the parent `Model` constructor.
   */
  constructor() {
    super();
  }
}
